import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../services/firebase';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px); /* Account for progress bar */
  padding-bottom: 0; /* Let BottomSpacer handle the spacing */
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
`;

const TextContent = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom:20px;
  text-align: center;
  
  p {
    margin-bottom: ${theme.spacing.md};
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  /* Support for HTML tags */
  strong, b {
    font-weight: ${theme.typography.weights.semiBold};
    color: ${theme.colors.text};
  }
  
  em, i {
    font-style: italic;
  }
  
  @media screen and (max-width: ${theme.breakpoints.mobileLg}) {
    font-size: ${theme.typography.sizes.body};
  }
`;

const LeaderboardContainer = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
  margin-bottom: 25px;
  width: 100%;
`;

const LeaderboardHeader = styled.div`
  background: ${theme.colors.primary};
  color: white;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.borderRadius.lg} ${theme.borderRadius.lg} 0 0;
`;

const LeaderboardTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  margin: 0;
  padding: 15px;
  line-height: 1;
  color: white;
`;

const LeaderboardSubtitle = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  margin: 0;
  opacity: 0.9;
`;

const LeaderboardList = styled.div`
  /* Remove max-height and overflow for pagination */
`;

const LeaderboardItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.doryLighterBlue};
  
  &:last-child {
    border-bottom: none;
  }
  
  &:nth-child(even) {
    background: ${theme.colors.doryBeige};
  }
  
  /* Mobile adjustments */
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    flex-wrap: nowrap;
    gap: ${theme.spacing.sm};
  }
`;

const Rank = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.doryBlue};
  min-width: 32px;
  text-align: center;
  margin-right: ${theme.spacing.md};
  
  /* Special styling for top 3 using brand colors */
  ${props => props.$rank === 1 && `
    color: ${theme.colors.doryGreen};
    font-size: ${theme.typography.sizes.h3};
  `}
  
  ${props => props.$rank === 2 && `
    color: ${theme.colors.doryDarkerBlue};
    font-size: ${theme.typography.sizes.bodyLarge};
  `}
  
  ${props => props.$rank === 3 && `
    color: ${theme.colors.primary};
    font-size: ${theme.typography.sizes.bodyLarge};
  `}
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    min-width: 24px;
    margin-right: ${theme.spacing.sm};
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 120px;
  max-width: 120px;
  flex-shrink: 0;
`;

const UserName = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.doryBlue};
  
  /* Truncate long names */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AnonymousName = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.light};
  color: ${theme.colors.textLight};
  font-style: italic;
`;

const UserShortcode = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.doryDarkerBlue};
  font-family: 'Courier New', monospace;
  letter-spacing: ${theme.typography.letterSpacing.wide};
`;

const ScoreColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 80px;
  max-width: 80px;
  flex-shrink: 0;
  margin-left: ${theme.spacing.md};
`;

const AggregateScore = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.bodyLarge};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.doryGreen};
  text-align: right;
`;

const DaysColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 100px;
  max-width: 100px;
  flex-shrink: 0;
  margin-left: ${theme.spacing.md};
`;

const DaysSince = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.doryDarkerBlue};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.doryDarkerBlue};
  font-family: ${theme.typography.bodyFont};
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.doryDarkerBlue};
  font-family: ${theme.typography.bodyFont};
  text-align: center;
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.doryDarkerBlue};
  font-family: ${theme.typography.bodyFont};
  text-align: center;
`;

const ContinueButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px;
  box-shadow: ${theme.shadows.sm};
  margin-top: 0;
  margin-bottom: 0; /* Let container padding handle spacing */
  min-width: 200px;
  width: fit-content;
  max-width: 90%;
  align-self: center;
  white-space: normal;
  line-height: 1.2;
  text-align: center;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  z-index: 10;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px;
    padding: 1rem 1.5rem;
    margin-bottom: 0; /* Let container padding handle spacing */
  }
  
  @media (max-width: 320px) {
    font-size: 0.875rem;
    padding: 0.75rem 1rem;
    min-width: 150px;
    margin-bottom: 0; /* Let container padding handle spacing */
  }
`;

const LoadMoreButton = styled.button`
  background: transparent;
  color: ${theme.colors.doryBlue};
  border: 1px solid ${theme.colors.doryBlue};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.xl};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  margin: 0 auto;
  display: block;
  width: fit-content;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background: ${theme.colors.doryBeige};
    color: ${theme.colors.doryLighterBlue};
    border-color: ${theme.colors.doryLighterBlue};
    cursor: not-allowed;
    transform: none;
  }
`;

const BottomSpacer = styled.div`
  height: 60px;
  width: 100%;
  flex-shrink: 0;
`;

const LeaderboardScreen = ({ data, onNext, userProgress = {} }) => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleRows, setVisibleRows] = useState(5); // Show 5 rows initially
  const rowsPerLoad = 5; // Load 5 more rows each time

  const handleContinue = () => {
    onNext?.(data.key);
  };

  // Get formatted HTML content
  const htmlContent = formatTextContent(data.content?.text, userProgress);

  // Fetch leaderboard data
  useEffect(() => {
    const fetchLeaderboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get all users from Firestore
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const users = [];
        let totalUsers = 0;
             let usersWithOptimalPace = 0;
             let usersWithMentalResilience = 0;
             let usersWithEnoughEntries = 0;
             let usersWithActiveStatus = 0;
             let usersWithLastActiveField = 0;
             let usersMeetingAllCriteria = 0;
        
        
        usersSnapshot.forEach((doc) => {
          const userData = doc.data();
          const shortcode = doc.id;
          totalUsers++;
          
          
               // Check if user meets criteria
               const hasOptimalPace = userData.calculatedMetrics?.optimalPace?.optimalHours &&
                                     userData.calculatedMetrics.optimalPace.optimalHours > 0;
               const hasMentalResilience = userData.calculatedMetrics?.mentalResilience?.score &&
                                          userData.calculatedMetrics.mentalResilience.score > 0;
               const hasEnoughJournalEntries = userData.journalEntries &&
                                              userData.journalEntries.length >= 3;
               
               // Check if user is active (lastActive date is September 24th or in the future)
               const isActive = userData.lastActive ? 
                 userData.lastActive.toDate() >= new Date('2025-09-24') : false;
               
          
          if (hasOptimalPace) usersWithOptimalPace++;
          if (hasMentalResilience) usersWithMentalResilience++;
          if (hasEnoughJournalEntries) usersWithEnoughEntries++;
          if (userData.lastActive) usersWithLastActiveField++;
          if (isActive) usersWithActiveStatus++;
          
               if (hasOptimalPace && hasMentalResilience && hasEnoughJournalEntries && isActive) {
            usersMeetingAllCriteria++;
            const optimalPace = userData.calculatedMetrics.optimalPace.optimalHours;
            const mentalResilience = userData.calculatedMetrics.mentalResilience.score;
            const aggregateScore = optimalPace * mentalResilience;
            
            // Get most recent journal entry date
            const journalEntries = userData.journalEntries || [];
            const mostRecentEntry = journalEntries.reduce((latest, entry) => {
              return entry.timestamp > latest.timestamp ? entry : latest;
            }, journalEntries[0]);
            
            users.push({
              shortcode,
              name: userData.userInfo?.name || null,
              aggregateScore,
              optimalPace,
              mentalResilience,
              lastEntryDate: mostRecentEntry?.timestamp || null,
              journalEntryCount: journalEntries.length
            });
          }
        });

        // Sort by aggregate score (descending)
        users.sort((a, b) => b.aggregateScore - a.aggregateScore);
        
        // Limit to top 100 users
        const topUsers = users.slice(0, 100);
        
        
        
        setLeaderboardData(topUsers);
      } catch (err) {
        console.error('Error fetching leaderboard data:', err);
        setError('Failed to load leaderboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboardData();
  }, []);

  // Debug when leaderboardData changes

  const formatDaysSince = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const lastEntryDate = new Date(timestamp);
    const today = new Date();
    const timeDiff = today.getTime() - lastEntryDate.getTime();
    const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));

    if (daysDiff === 0) return 'Today';
    if (daysDiff === 1) return '1 day ago';
    return `${daysDiff} days ago`;
  };

  const getOrdinalSuffix = (num) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };

  const formatScore = (score) => {
    return Math.round(score).toLocaleString();
  };

  const getDisplayName = (user) => {
    if (user.name) {
      // Split the name into parts
      const nameParts = user.name.trim().split(' ');
      
      if (nameParts.length === 1) {
        // Only one name part, return as is
        return nameParts[0];
      } else if (nameParts.length >= 2) {
        // Multiple parts, show first name + last initial
        const firstName = nameParts[0];
        const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
        return `${firstName} ${lastInitial}.`;
      }
    }
    return 'Anonymous';
  };

  const isAnonymous = (user) => {
    return !user.name;
  };

  // Cumulative loading logic
  const currentRows = leaderboardData.slice(0, visibleRows);
  const hasMoreRows = visibleRows < leaderboardData.length;

  // Calculate user's rank and score
  const getUserRank = () => {
    if (!userProgress?.shortcode || !leaderboardData.length) return null;
    
    const userIndex = leaderboardData.findIndex(user => user.shortcode === userProgress.shortcode);
    if (userIndex === -1) return null;
    
    const user = leaderboardData[userIndex];
    return {
      rank: userIndex + 1,
      score: user.aggregateScore,
      totalCompetitors: leaderboardData.length
    };
  };

  const userRank = getUserRank();


  const handleLoadMore = () => {
    if (hasMoreRows) {
      setVisibleRows(prev => Math.min(prev + rowsPerLoad, leaderboardData.length));
    }
  };

  return (
    <Container>
      <Content>
        {htmlContent && (
          <TextContent dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}
        
        {userRank && (
          <div style={{
            padding: `0 ${theme.spacing.lg} ${theme.spacing.md} ${theme.spacing.lg}`,
            marginBottom: '16px',
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.colors.border}`
          }}>
            <div style={{
              fontFamily: theme.typography.bodyFont,
              fontSize: theme.typography.sizes.body,
              color: theme.colors.text,
              lineHeight: theme.typography.lineHeights.normal,
              textAlign: 'center'
            }}>
                Your score of <strong>{formatScore(userRank.score)}</strong> has earned you <strong>{userRank.rank}{getOrdinalSuffix(userRank.rank)} place</strong> among <strong>{userRank.totalCompetitors}</strong> total competitors in the challenge.
            </div>
          </div>
        )}

        <LeaderboardContainer>
          <LeaderboardHeader>
            <LeaderboardTitle>Superhuman Challenge Leaderboard</LeaderboardTitle>
          </LeaderboardHeader>
          
          {loading && (
            <LoadingContainer>
              Loading leaderboard...
            </LoadingContainer>
          )}
          
          {error && (
            <ErrorContainer>
              {error}
            </ErrorContainer>
          )}
          
          {!loading && !error && leaderboardData.length === 0 && (
            <EmptyState>
              No community members meet the leaderboard criteria yet.
            </EmptyState>
          )}
          
          {!loading && !error && leaderboardData.length > 0 && (
            <LeaderboardList>
              {currentRows.map((user, index) => (
                <LeaderboardItem key={user.shortcode}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
                    <Rank $rank={index + 1}>
                      {index + 1}
                    </Rank>
                    
                    <UserInfo>
                      {isAnonymous(user) ? (
                        <AnonymousName>{getDisplayName(user)}</AnonymousName>
                      ) : (
                        <UserName>{getDisplayName(user)}</UserName>
                      )}
                    </UserInfo>
                  </div>
                  
                  <ScoreColumn>
                    <AggregateScore>
                      {formatScore(user.aggregateScore)}
                    </AggregateScore>
                  </ScoreColumn>
                  
                  <DaysColumn>
                    <DaysSince>
                      {formatDaysSince(user.lastEntryDate)}
                    </DaysSince>
                  </DaysColumn>
                </LeaderboardItem>
              ))}
            </LeaderboardList>
          )}
        </LeaderboardContainer>
        
        {hasMoreRows && (
          <div style={{ marginBottom: '30px' }}>
            <LoadMoreButton onClick={handleLoadMore}>
              Load More
            </LoadMoreButton>
          </div>
        )}
        
        <ContinueButton onClick={handleContinue}>
          {data.content?.ctaText || 'Continue'}
        </ContinueButton>
        
        <BottomSpacer />
      </Content>
    </Container>
  );
};

export default LeaderboardScreen;
